import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { OrderSummary } from '#domain/Order.ts';
import { containsOrderSearchTerm } from '#utils/orderSearch.ts';

import {
    customOrderRange,
    matchesOrderTimeRange,
    type OrderPeriod,
    type OrderTimeRange,
    resolveOrderPeriod,
} from './orderFilters.ts';

export const useOrderListFilters = (orders: OrderSummary[], isOpen: boolean, showServiceOrders: boolean) => {
    const intl = useIntl();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [types, setTypes] = useState<string[]>(['transport', 'service']);
    const [period, setPeriod] = useState<OrderPeriod>('all');
    const [appliedPeriod, setAppliedPeriod] = useState<Exclude<OrderPeriod, 'custom'>>('all');
    const [from, setFrom] = useState<Date>();
    const [to, setTo] = useState<Date>();
    const [customRange, setCustomRange] = useState<OrderTimeRange>();
    const [invalidDates, setInvalidDates] = useState({ from: false, to: false });
    const [now, setNow] = useState(Date.now);

    const reset = useCallback(() => {
        setSearchInput('');
        setSearch('');
        setTypes(['transport', 'service']);
        setPeriod('all');
        setAppliedPeriod('all');
        setFrom(undefined);
        setTo(undefined);
        setCustomRange(undefined);
        setInvalidDates({ from: false, to: false });
        setNow(Date.now());
    }, []);

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    useEffect(() => {
        if (!isOpen || searchInput === search) {
            return;
        }
        const timeout = window.setTimeout(() => setSearch(searchInput), 300);
        return () => window.clearTimeout(timeout);
    }, [isOpen, searchInput, search]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        let timeout: ReturnType<typeof setTimeout>;
        const update = () => {
            clearTimeout(timeout);
            const current = DateTime.local();
            setNow(current.toMillis());
            const untilMidnight = current.plus({ days: 1 }).startOf('day').toMillis() - current.toMillis();
            timeout = setTimeout(update, Math.min(60_000, untilMidnight));
        };
        update();
        window.addEventListener('focus', update);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('focus', update);
        };
    }, [isOpen]);

    useEffect(() => {
        if (period !== 'custom') {
            setAppliedPeriod(period);
            setCustomRange(undefined);
        } else {
            const nextRange = customOrderRange(from, to);
            if (nextRange && !invalidDates.from && !invalidDates.to) {
                setCustomRange(nextRange);
            }
        }
    }, [period, from, to, invalidDates]);

    const range = customRange ?? resolveOrderPeriod(appliedPeriod, now);
    const visibleOrders = useMemo(
        () =>
            orders.filter(
                order =>
                    (showServiceOrders || order.type !== 'service') &&
                    types.includes(order.type ?? 'transport') &&
                    containsOrderSearchTerm(order, search, intl) &&
                    matchesOrderTimeRange(order, range)
            ),
        [orders, showServiceOrders, types, search, range?.start, range?.end, intl]
    );

    return {
        searchInput,
        setSearchInput,
        types,
        setTypes,
        period,
        setPeriod: (nextPeriod: OrderPeriod) => {
            setNow(Date.now());
            setPeriod(nextPeriod);
        },
        from,
        setFrom,
        to,
        setTo,
        reset,
        visibleOrders,
        invalidDates,
        setInvalidDates,
        invalidRange: period === 'custom' && Boolean(from && to) && !customOrderRange(from, to),
    };
};
